import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GenreBookType, LoanHistoryType } from "../types/Books";
import {
  NameMemberRecommendRequestPayload,
  RecommendBooksResponse,
  RecommendBooksResponseBody,
  UserInfoType,
} from "../types/User";

import { getUserAndBooks, getUserAndBooksByNameAndMemberNo } from "../network/user.api";
import String from "../configs/Strings";
import { Library_code } from "../types/Library";

export const PRB = "personalRecommendBookSlice";

export type PrbState = {
  userInfo: UserInfoType;
  loanHistory: LoanHistoryType;
  genreBooks: GenreBookType;
  failedLogin: string;
  library: Library_code | "";
};

type PayloadType = {
  [PRB]: PrbState;
};

type RecommendRequestSource = "login" | "direct";

export const fetchUserAndBooks = createAsyncThunk(`${PRB}/fetchUserAndBooks`, getUserAndBooks);
export const fetchUserAndBooksByNameAndMemberNo = createAsyncThunk(
  `${PRB}/fetchUserAndBooksByNameAndMemberNo`,
  (payload: NameMemberRecommendRequestPayload, { signal }) =>
    getUserAndBooksByNameAndMemberNo(payload, signal)
);

const NO_RECOMMEND_RESULT_MESSAGE = "조회 결과가 없습니다. 사용자 정보를 확인해주세요.";
const DEFAULT_USER_INFO = { userName: "", age: -1 };

const getDefaultGenreBooks = (): GenreBookType => ({
  총류: [],
  철학: [],
  종교: [],
  사회과학: [],
  자연과학: [],
  기술과학: [],
  예술: [],
  언어: [],
  문학: [],
  역사: [],
});

const getDefaultLoanHistory = (): LoanHistoryType => ({
  favoriteGenre: {},
  totalLoanQuantity: 0,
});

const normalizeRecommendBooksResponseBody = (
  responseBody: Partial<RecommendBooksResponseBody>
): RecommendBooksResponseBody | null => {
  const { genreBooks, loanHistory, userInfo } = responseBody;

  if (!userInfo?.userName) {
    return null;
  }

  const defaultLoanHistory = getDefaultLoanHistory();

  return {
    genreBooks: { ...getDefaultGenreBooks(), ...genreBooks },
    loanHistory: {
      favoriteGenre: loanHistory?.favoriteGenre ?? defaultLoanHistory.favoriteGenre,
      totalLoanQuantity: loanHistory?.totalLoanQuantity ?? defaultLoanHistory.totalLoanQuantity,
    },
    userInfo: {
      userName: userInfo.userName,
      age: userInfo.age ?? DEFAULT_USER_INFO.age,
    },
  };
};

const buildRestrictedLoginMessage = (description: string | null) => {
  if (!description) {
    return `${String.ERROR.LOGIN.RESTRICTED}\n잠시 후 다시 시도해주세요.`;
  }

  const searchWord = "after";
  const searchPosition = description.search(searchWord);
  if (searchPosition === -1) {
    return `${String.ERROR.LOGIN.RESTRICTED}\n잠시 후 다시 시도해주세요.`;
  }

  const blockTime = description.substring(searchPosition + searchWord.length).trim();
  if (!blockTime) {
    return `${String.ERROR.LOGIN.RESTRICTED}\n잠시 후 다시 시도해주세요.`;
  }

  return `${String.ERROR.LOGIN.RESTRICTED} \n ${blockTime} 이후 다시 시도해주세요.`;
};

const applyRecommendBooksResponse = (
  state: PrbState,
  payload: RecommendBooksResponse,
  requestSource: RecommendRequestSource
) => {
  const { status_code, description } = payload;
  if (!payload.response_body) {
    if (requestSource === "direct" && status_code === 9404) {
      state.failedLogin = String.ERROR.DIRECT_RECOMMEND.USER_NOT_FOUND;
    } else if (requestSource === "direct" && status_code === 3406) {
      state.failedLogin = String.ERROR.DIRECT_RECOMMEND.RESTRICTED;
    } else if (status_code === 9404) {
      state.failedLogin = String.ERROR.LOGIN.NOT_VAILD;
    } else if (status_code === 3406) {
      state.failedLogin = buildRestrictedLoginMessage(description);
    } else if (status_code === 0) {
      state.failedLogin = NO_RECOMMEND_RESULT_MESSAGE;
    } else {
      state.failedLogin = "통신이 불안정합니다. 잠시 후 다시 시도해주세요.";
    }
    return;
  }

  const normalizedResponseBody = normalizeRecommendBooksResponseBody(payload.response_body);
  if (!normalizedResponseBody) {
    state.failedLogin = NO_RECOMMEND_RESULT_MESSAGE;
    return;
  }

  state.genreBooks = normalizedResponseBody.genreBooks;
  state.loanHistory = normalizedResponseBody.loanHistory;
  state.userInfo = normalizedResponseBody.userInfo;

  window.sessionStorage.setItem(
    "data",
    JSON.stringify({
      genreBooks: state.genreBooks,
      loanHistory: state.loanHistory,
      userInfo: state.userInfo,
    })
  );
};

const personalRecommendBookSlice = createSlice({
  name: `${PRB}`,
  initialState: {
    userInfo: DEFAULT_USER_INFO,
    loanHistory: { favoriteGenre: {}, totalLoanQuantity: -1 },
    genreBooks: getDefaultGenreBooks(),
    failedLogin: "",
    library: "",
  } as PrbState,
  reducers: {
    resetUser: (state) => {
      state.userInfo = DEFAULT_USER_INFO;
      state.loanHistory = { favoriteGenre: {}, totalLoanQuantity: -1 };
      state.genreBooks = getDefaultGenreBooks();
      state.failedLogin = "";
      state.library = "";
    },
    saveSessionValue: (state, action) => {
      const normalizedResponseBody = normalizeRecommendBooksResponseBody(action.payload);
      if (!normalizedResponseBody) return;

      state.genreBooks = normalizedResponseBody.genreBooks;
      state.loanHistory = normalizedResponseBody.loanHistory;
      state.userInfo = normalizedResponseBody.userInfo;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserAndBooks.fulfilled, (state, action) => {
      applyRecommendBooksResponse(state, action.payload, "login");
    });
    builder.addCase(fetchUserAndBooksByNameAndMemberNo.fulfilled, (state, action) => {
      applyRecommendBooksResponse(state, action.payload, "direct");
    });
    builder.addCase(fetchUserAndBooks.rejected, (state, action) => {
      state.failedLogin = "통신이 불안정합니다. 잠시 후 다시 시도해주세요.";
      sessionStorage.clear();
    });
    builder.addCase(fetchUserAndBooksByNameAndMemberNo.rejected, (state, action) => {
      if (action.meta.aborted) return;
      state.failedLogin = "통신이 불안정합니다. 잠시 후 다시 시도해주세요.";
      sessionStorage.clear();
    });
  },
});

export default personalRecommendBookSlice.reducer;

export const { resetUser, saveSessionValue } = personalRecommendBookSlice.actions;
export const selectPRBSlice = (state: PayloadType) => state[PRB];
